import { find, size, sortBy } from 'es-toolkit/compat';
import { assertNever } from './typeUtils';
import { core } from '@tock/coretypes';
import { findOrdersMatchingOptions, getIsExcluded, getSelectedOptionsCount } from './option';

export const getToGoCardGroupData = ({
  optionOrders,
  partyOptionOrders,
  ticketOptions,
  ticketOptionGroup,
  partySize,
}: {
  optionOrders: core.IOptionOrder[];
  partyOptionOrders?: core.IOptionOrder[];
  ticketOptions: core.ITicketOption[];
  ticketOptionGroup: core.ITicketOptionGroup;
  partySize: number;
}) => {
  const sortedTicketOptions = sortBy(ticketOptions, 'ordering');
  const numSelectedInGroup = getSelectedOptionsCount(
    [...optionOrders, ...(partyOptionOrders || [])],
    ticketOptions as core.TicketOption[]
  );

  return {
    isOptionGroupMaxed: isOptionGroupMaxed(ticketOptionGroup, partySize, numSelectedInGroup),
    sortedTicketOptions,
  };
};

function isOptionGroupMaxed(
  optionGroup: core.ITicketOptionGroup,
  partySize: number,
  numSelectedInGroup: number
): boolean {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  switch (optionGroupType) {
    //PER_PARTY handled by isOptionMaxed
    case 'PER_PARTY':
      return false;
    case 'UNLIMITED':
      return false;
    case 'FIXED_LIMIT':
      return (optionGroup.fixedLimitMax ?? 1) - numSelectedInGroup === 0;
    case 'PER_PERSON':
      return partySize - numSelectedInGroup === 0;
    default:
      assertNever(optionGroupType);
  }
}

export const getToGoCardData = ({
  ticketOption,
  ticketOptionGroup,
  ticketOptions,
  ticketOptionPrices,
  optionOrders,
  optionExclusion,
  useInclusivePricing,
}: {
  ticketOption: core.ITicketOption;
  ticketOptionGroup: core.ITicketOptionGroup;
  ticketOptions: core.ITicketOption[];
  ticketOptionPrices: core.IPrice[];
  optionOrders: core.IOptionOrder[];
  optionExclusion: core.IOptionExclusion[] | null | undefined;
  useInclusivePricing: boolean;
}) => {
  const ticketOptionId = ticketOption.id as number;
  const numSelectedOfSupplement = size(findOrdersMatchingOptions(optionOrders, [ticketOption]));

  const relevantTicketOptionPrice = find(ticketOptionPrices, {
    id: ticketOption.id,
    backingMode: ticketOption.backingMode,
  });
  const price =
    (useInclusivePricing
      ? relevantTicketOptionPrice?.amountIncludingFees
      : relevantTicketOptionPrice?.amount) ?? 0;

  const isExcluded = getIsExcluded(
    ticketOptionId,
    ticketOptionGroup,
    ticketOptions,
    optionOrders,
    optionExclusion
  );
  return {
    isSoldOut:
      typeof ticketOption.currentQuantity === 'number' &&
      ticketOption.currentQuantity <= numSelectedOfSupplement,
    isOptionMaxed: isOptionMaxed(ticketOptionGroup, numSelectedOfSupplement),
    isExcluded,
    numSelectedOfSupplement,
    price,
  };
};

function isOptionMaxed(optionGroup: core.ITicketOptionGroup, numSelectedOfOption: number) {
  const optionGroupType = optionGroup.optionGroupType as core.OptionGroupType;
  return optionGroupType === 'PER_PARTY' && numSelectedOfOption >= 1;
}
